import { useState, useMemo } from 'react';
import axios from 'axios';
import UserFilterBar from './UserFilterBar';
import UsersTable from './UsersTable';
import ConfirmModal from './ConfirmModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ACTION_COPY = {
  suspend: { title: 'Suspend User?', body: 'The user will be blocked from logging in until reactivated.', label: 'Suspend', variant: 'warning' },
  reactivate: { title: 'Reactivate User?', body: 'The user will be able to log in again immediately.', label: 'Reactivate', variant: 'success' },
  delete: { title: 'Delete User?', body: 'This will permanently delete the user, their tournaments, and all associated registrations. This action cannot be undone.', label: 'Delete', variant: 'danger' }
};

const AdminUsersTab = ({ users, userTypeFilter, setUserTypeFilter, search, setSearch, onRefresh }) => {
  const [confirm, setConfirm] = useState(null); // { type, user }
  const [busy, setBusy] = useState(false);

  const filtered = useMemo(() => {
    const needle = search.toLowerCase();
    return users.filter((u) => {
      if (userTypeFilter !== 'all' && u.role !== userTypeFilter) return false;
      if (needle && !`${u.name} ${u.email} ${u.city || ''}`.toLowerCase().includes(needle)) return false;
      return true;
    });
  }, [users, userTypeFilter, search]);

  const requestAction = (type, user) => setConfirm({ type, user });

  const runAction = async () => {
    if (!confirm) return;
    setBusy(true);
    const { type, user } = confirm;
    const endpoints = {
      suspend: () => axios.post(`${API}/admin/users/${user._id}/suspend`, {}, { withCredentials: true }),
      reactivate: () => axios.post(`${API}/admin/users/${user._id}/reactivate`, {}, { withCredentials: true }),
      delete: () => axios.delete(`${API}/admin/users/id/${user._id}`, { withCredentials: true })
    };
    try {
      await endpoints[type]();
      setConfirm(null);
      onRefresh && onRefresh();
    } catch (e) {
      window.alert(e.response?.data?.detail || `Failed to ${type} user`);
    } finally {
      setBusy(false);
    }
  };

  const copy = confirm ? ACTION_COPY[confirm.type] : null;

  return (
    <div className="space-y-4 animate-fade-in">
      <UserFilterBar
        userTypeFilter={userTypeFilter}
        setUserTypeFilter={setUserTypeFilter}
        search={search}
        setSearch={setSearch}
      />

      <UsersTable users={filtered} onRequestAction={requestAction} />

      <ConfirmModal
        open={!!confirm}
        title={copy?.title || ''}
        description={copy?.body || ''}
        confirmLabel={copy?.label || 'Confirm'}
        variant={copy?.variant || 'danger'}
        onCancel={() => setConfirm(null)}
        onConfirm={runAction}
        busy={busy}
        zClass="z-50"
        testId="confirm-user-action-modal"
      />
    </div>
  );
};

export default AdminUsersTab;
